import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Button,
  Card,
  CardContent,
  Checkbox,
  CircularProgress,
  Collapse,
  IconButton,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  MenuItem,
  TextField,
  Typography,
  Alert,
  Chip,
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  ExpandLess,
  ExpandMore,
  Refresh as RefreshIcon,
  Settings as SettingsIcon,
} from '@mui/icons-material';
import { useSnackbar } from 'notistack';
import Can from '../../../components/Can';
import CurrencyTextField from '../../../components/shared/CurrencyTextField';
import PDFViewer from '../../../components/shared/PDFViewer';
import { ROUTES } from '../../../config/routes';
import contratoVentaService, {
  ContratoFacturacionConfirmacionResult,
  ContratoFacturacionPreview,
  ContratoFacturacionPreviewGrupo,
  ContratoFacturacionPreviewLinea,
  ContratoTipo,
} from '../../../services/contratoVentaService';
import ContratoTiposDialog from './ContratoTiposDialog';
import { fetchFacturaVentaPdf } from '../../../utils/facturaVentaPdf';
import { formatCurrency, toLocalDateStringYMD } from '../../../utils/formatters';

type EditableLinea = ContratoFacturacionPreviewLinea & { seleccionada: boolean };

type EditableGrupo = Omit<ContratoFacturacionPreviewGrupo, 'lineas'> & {
  lineas: EditableLinea[];
  cotizacion: number;
  expandido: boolean;
};

const recalcLineaImportes = (
  linea: EditableLinea,
  cotizacion: number,
): EditableLinea => {
  const cot = cotizacion > 0 ? cotizacion : 1;
  const bruto = linea.cantidad * linea.precioUnitarioMoneda;
  const neto = Math.round((bruto - bruto * (linea.bonificacion / 100)) * 100) / 100;
  const iva = Math.round(neto * (linea.porcentajeIva / 100) * 100) / 100;
  const conIva = Math.round((neto + iva) * 100) / 100;
  return {
    ...linea,
    importeMoneda: neto,
    importePesos: Math.round(neto * cot * 100) / 100,
    importeIvaMoneda: iva,
    importeConIvaMoneda: conIva,
    importeConIvaPesos: Math.round(conIva * cot * 100) / 100,
    cotizacion: cot,
  };
};

const ContratosFacturacionPreview: React.FC = () => {
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();
  const [fechaCorte, setFechaCorte] = useState(toLocalDateStringYMD());
  const [tipoContratoId, setTipoContratoId] = useState('');
  const [tipos, setTipos] = useState<ContratoTipo[]>([]);
  const [tiposOpen, setTiposOpen] = useState(false);
  const [fechaEmision, setFechaEmision] = useState(toLocalDateStringYMD());
  const [emitirConCae, setEmitirConCae] = useState(true);
  const [loading, setLoading] = useState(false);
  const [confirmando, setConfirmando] = useState(false);
  const [grupos, setGrupos] = useState<EditableGrupo[]>([]);
  const [resultado, setResultado] = useState<ContratoFacturacionConfirmacionResult | null>(null);
  const [pdfUrl, setPdfUrl] = useState<string | null>(null);
  const [pdfDocId, setPdfDocId] = useState<string>('');

  const loadPreview = useCallback(async () => {
    try {
      setLoading(true);
      setResultado(null);
      const preview: ContratoFacturacionPreview = await contratoVentaService.previewFacturacion(
        fechaCorte,
        tipoContratoId ? Number(tipoContratoId) : undefined,
      );
      setGrupos(
        (preview.contratos || []).map((g) => ({
          ...g,
          cotizacion: g.cotizacionSugerida || 1,
          expandido: true,
          lineas: (g.lineas || []).map((l) => ({
            ...l,
            porcentajeIva: l.porcentajeIva ?? 0,
            importeIvaMoneda: l.importeIvaMoneda ?? 0,
            importeConIvaMoneda: l.importeConIvaMoneda ?? l.importeMoneda,
            importeConIvaPesos: l.importeConIvaPesos ?? l.importePesos,
            seleccionada: l.seleccionada !== false,
          })),
        }))
      );
    } catch (error: any) {
      enqueueSnackbar(error?.response?.data?.message || 'Error al previsualizar', { variant: 'error' });
      setGrupos([]);
    } finally {
      setLoading(false);
    }
  }, [fechaCorte, tipoContratoId, enqueueSnackbar]);

  const cargarTipos = useCallback(async () => {
    try {
      setTipos(await contratoVentaService.getTipos());
    } catch {
      setTipos([]);
    }
  }, []);

  useEffect(() => {
    void cargarTipos();
  }, [cargarTipos]);

  useEffect(() => {
    loadPreview();
  }, [loadPreview]);

  const updateLinea = (contratoId: number, index: number, patch: Partial<EditableLinea>) => {
    setGrupos((prev) =>
      prev.map((g) => {
        if (g.contratoId !== contratoId) return g;
        const lineas = g.lineas.map((l, i) => {
          if (i !== index) return l;
          return recalcLineaImportes({ ...l, ...patch }, g.cotizacion);
        });
        return { ...g, lineas };
      })
    );
  };

  const updateCotizacion = (contratoId: number, cotizacion: number) => {
    setGrupos((prev) =>
      prev.map((g) => {
        if (g.contratoId !== contratoId) return g;
        const cot = cotizacion > 0 ? cotizacion : 1;
        return {
          ...g,
          cotizacion: cot,
          lineas: g.lineas.map((l) => recalcLineaImportes(l, cot)),
        };
      })
    );
  };

  const setSeleccionTodas = (seleccionada: boolean) => {
    setGrupos((prev) =>
      prev.map((g) => ({
        ...g,
        lineas: g.lineas.map((l) => ({ ...l, seleccionada })),
      }))
    );
  };

  const seleccionados = useMemo(
    () =>
      grupos
        .map((g) => ({
          ...g,
          lineas: g.lineas.filter((l) => l.seleccionada),
        }))
        .filter((g) => g.lineas.length > 0),
    [grupos]
  );

  const totalPesos = seleccionados.reduce(
    (acc, g) => acc + g.lineas.reduce((s, l) => s + (l.importeConIvaPesos ?? l.importePesos), 0),
    0
  );

  const handleConfirmar = async () => {
    if (seleccionados.length === 0) {
      enqueueSnackbar('Seleccioná al menos una línea', { variant: 'warning' });
      return;
    }
    try {
      setConfirmando(true);
      const result = await contratoVentaService.confirmarFacturacion({
        fechaEmision,
        emitirConCae,
        contratos: seleccionados.map((g) => ({
          contratoId: g.contratoId,
          cotizacion: g.cotizacion,
          lineas: g.lineas.map((l) => ({
            tipo: l.tipo,
            contratoId: l.contratoId,
            contratoPeriodoId: l.contratoPeriodoId,
            contratoCertificacionId: l.contratoCertificacionId,
            contratoItemId: l.contratoItemId,
            codigoProducto: l.codigoProducto,
            descripcion: l.descripcionResuelta,
            cantidad: l.cantidad,
            precioUnitarioMoneda: l.precioUnitarioMoneda,
            bonificacion: l.bonificacion,
            cotizacion: g.cotizacion,
            nroOrdenCompra: l.nroOrdenCompra,
          })),
        })),
      });
      setResultado(result);
      enqueueSnackbar(
        `Facturación: ${result.okCount} ok, ${result.errorCount} con error`,
        { variant: result.errorCount > 0 ? 'warning' : 'success' }
      );
      if (result.okCount > 0) await loadPreview();
    } catch (error: any) {
      enqueueSnackbar(error?.response?.data?.message || 'Error al facturar', { variant: 'error' });
    } finally {
      setConfirmando(false);
    }
  };

  const openPdf = async (nroFormulario: number) => {
    try {
      const result = await fetchFacturaVentaPdf(nroFormulario);
      setPdfUrl(result.url);
      setPdfDocId(String(nroFormulario));
    } catch {
      enqueueSnackbar('No se pudo obtener el PDF', { variant: 'error' });
    }
  };

  return (
    <Box sx={{ p: { xs: 2, sm: 3 }, bgcolor: '#f5f5f5', minHeight: '100vh' }}>
      <Card sx={{ bgcolor: '#fff', borderRadius: 3, boxShadow: '0 2px 8px rgba(0,0,0,0.1)' }}>
        <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
          <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 2 }}>
            <IconButton onClick={() => navigate(ROUTES.ventas.contratos.list)}>
              <ArrowBackIcon />
            </IconButton>
            <Typography variant="h4" component="h1" sx={{ fontWeight: 600, color: '#1976d2', flex: 1 }}>
              Facturación masiva de contratos
            </Typography>
          </Stack>

          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Se evalúan contratos vigentes con períodos o certificaciones vencidas a la fecha de corte.
            El precio unitario es el fijo o de lista; luego se aplica la bonificación y el IVA según el
            cliente y el producto. Los importes se convierten a pesos antes de emitir en ARCA.
          </Typography>

          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} sx={{ mb: 2 }} alignItems="center">
            <TextField
              type="date"
              label="Fecha de corte"
              size="small"
              InputLabelProps={{ shrink: true }}
              value={fechaCorte}
              onChange={(e) => setFechaCorte(e.target.value)}
            />
            <Stack direction="row" spacing={0.5} alignItems="flex-start">
              <TextField
                select
                size="small"
                label="Tipo de contrato"
                value={tipoContratoId}
                onChange={(e) => setTipoContratoId(e.target.value)}
                sx={{ minWidth: 200 }}
              >
                <MenuItem value="">Todos</MenuItem>
                {tipos.filter((t) => t.activo).map((t) => (
                  <MenuItem key={t.id} value={String(t.id)}>
                    {t.nombre}
                  </MenuItem>
                ))}
              </TextField>
              <IconButton
                size="small"
                title="Configurar tipos"
                aria-label="Configurar tipos de contrato"
                onClick={() => setTiposOpen(true)}
                sx={{ mt: 0.5 }}
              >
                <SettingsIcon fontSize="small" />
              </IconButton>
            </Stack>
            <TextField
              type="date"
              label="Fecha emisión"
              size="small"
              InputLabelProps={{ shrink: true }}
              value={fechaEmision}
              onChange={(e) => setFechaEmision(e.target.value)}
            />
            <Stack direction="row" alignItems="center">
              <Checkbox checked={emitirConCae} onChange={(e) => setEmitirConCae(e.target.checked)} />
              <Typography variant="body2">Emitir con CAE (ARCA)</Typography>
            </Stack>
            <Button startIcon={<RefreshIcon />} onClick={loadPreview} disabled={loading}>
              Actualizar
            </Button>
            {grupos.length > 0 && (
              <>
                <Button
                  size="small"
                  variant="outlined"
                  onClick={() => setSeleccionTodas(true)}
                  disabled={loading || confirmando}
                >
                  Tildar todo
                </Button>
                <Button
                  size="small"
                  variant="outlined"
                  onClick={() => setSeleccionTodas(false)}
                  disabled={loading || confirmando}
                >
                  Destildar todo
                </Button>
              </>
            )}
            <Box sx={{ flex: 1 }} />
            <Typography variant="subtitle1" fontWeight={700}>
              Total seleccionado: {formatCurrency(totalPesos)}
            </Typography>
            <Can permiso="ventas.contratos.facturar">
              <Button
                variant="contained"
                onClick={handleConfirmar}
                disabled={confirmando || loading || seleccionados.length === 0}
              >
                {confirmando ? 'Facturando…' : 'Confirmar facturación'}
              </Button>
            </Can>
          </Stack>

          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
              <CircularProgress />
            </Box>
          ) : grupos.length === 0 ? (
            <Alert severity="info">No hay períodos ni avances pendientes a facturar a la fecha de corte.</Alert>
          ) : (
            grupos.map((g) => (
              <Card key={g.contratoId} variant="outlined" sx={{ mb: 2, borderRadius: 2 }}>
                <Box
                  sx={{
                    px: 2,
                    py: 1.5,
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1,
                    bgcolor: '#fafafa',
                    cursor: 'pointer',
                  }}
                  onClick={() =>
                    setGrupos((prev) =>
                      prev.map((x) =>
                        x.contratoId === g.contratoId ? { ...x, expandido: !x.expandido } : x
                      )
                    )
                  }
                >
                  <IconButton size="small">
                    {g.expandido ? <ExpandLess /> : <ExpandMore />}
                  </IconButton>
                  <Typography fontWeight={600} sx={{ flex: 1 }}>
                    Contrato {g.numero}
                    {g.codigoContrato ? ` (${g.codigoContrato})` : ''} — {g.nroCliente} {g.nombreCliente || ''}
                  </Typography>
                  {g.tipoContratoNombre && <Chip size="small" label={g.tipoContratoNombre} />}
                  <Chip size="small" label={g.monedaDescripcion || `Mon ${g.codigoMoneda}`} />
                  {g.codigoMoneda !== 0 && (
                    <TextField
                      size="small"
                      label="Cotización"
                      type="number"
                      value={g.cotizacion}
                      onClick={(e) => e.stopPropagation()}
                      onChange={(e) => updateCotizacion(g.contratoId, Number(e.target.value) || 1)}
                      sx={{ width: 120 }}
                    />
                  )}
                  <Typography variant="body2">
                    {formatCurrency(
                      g.lineas
                        .filter((l) => l.seleccionada)
                        .reduce((s, l) => s + (l.importeConIvaPesos ?? l.importePesos), 0)
                    )}
                  </Typography>
                </Box>
                <Collapse in={g.expandido}>
                  <TableContainer>
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell padding="checkbox" />
                          <TableCell>Tipo</TableCell>
                          <TableCell>Producto</TableCell>
                          <TableCell>Descripción</TableCell>
                          <TableCell align="right">Cant.</TableCell>
                          <TableCell align="right">Precio</TableCell>
                          <TableCell align="right">% Bonif.</TableCell>
                          <TableCell align="right">Neto $</TableCell>
                          <TableCell align="right">IVA %</TableCell>
                          <TableCell align="right">Total $</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {g.lineas.map((l, idx) => (
                          <TableRow key={`${l.tipo}-${l.contratoPeriodoId || l.contratoCertificacionId}-${idx}`}>
                            <TableCell padding="checkbox">
                              <Checkbox
                                checked={l.seleccionada}
                                onChange={(e) =>
                                  updateLinea(g.contratoId, idx, { seleccionada: e.target.checked })
                                }
                              />
                            </TableCell>
                            <TableCell>
                              {l.tipo === 'Periodo' ? `Cuota ${l.nroCuota}` : `Avance ${l.porcentajeAvance}%`}
                            </TableCell>
                            <TableCell>{l.codigoProducto}</TableCell>
                            <TableCell sx={{ minWidth: 280, maxWidth: 480 }}>
                              <TextField
                                size="small"
                                fullWidth
                                multiline
                                minRows={1}
                                maxRows={6}
                                value={l.descripcionResuelta}
                                onChange={(e) =>
                                  updateLinea(g.contratoId, idx, { descripcionResuelta: e.target.value })
                                }
                                sx={{
                                  '& .MuiInputBase-input': {
                                    whiteSpace: 'pre-wrap',
                                    wordBreak: 'break-word',
                                  },
                                }}
                              />
                            </TableCell>
                            <TableCell align="right">{l.cantidad}</TableCell>
                            <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>
                              <CurrencyTextField
                                size="small"
                                value={l.precioUnitarioMoneda}
                                onChange={(value) =>
                                  updateLinea(g.contratoId, idx, {
                                    precioUnitarioMoneda: value,
                                  })
                                }
                                sx={{ width: 140 }}
                                inputProps={{ style: { textAlign: 'right' } }}
                              />
                            </TableCell>
                            <TableCell align="right">{l.bonificacion}</TableCell>
                            <TableCell align="right">{formatCurrency(l.importePesos)}</TableCell>
                            <TableCell align="right">{l.porcentajeIva ?? 0}</TableCell>
                            <TableCell align="right">
                              {formatCurrency(l.importeConIvaPesos ?? l.importePesos)}
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Collapse>
              </Card>
            ))
          )}

          {resultado && (
            <Box sx={{ mt: 3 }}>
              <Typography variant="h6" sx={{ mb: 1 }}>
                Resultado
              </Typography>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Contrato</TableCell>
                    <TableCell>Estado</TableCell>
                    <TableCell>Factura</TableCell>
                    <TableCell>CAE</TableCell>
                    <TableCell>Mensaje</TableCell>
                    <TableCell />
                  </TableRow>
                </TableHead>
                <TableBody>
                  {resultado.resultados.map((r) => (
                    <TableRow key={r.contratoId}>
                      <TableCell>{r.numero}</TableCell>
                      <TableCell>
                        <Chip
                          size="small"
                          color={r.ok ? 'success' : 'error'}
                          label={r.ok ? 'OK' : 'Error'}
                        />
                      </TableCell>
                      <TableCell>{r.nroFormulario || '—'}</TableCell>
                      <TableCell>{r.caeNro || '—'}</TableCell>
                      <TableCell>{r.mensaje || '—'}</TableCell>
                      <TableCell>
                        {r.ok && r.nroFormulario ? (
                          <Button size="small" onClick={() => openPdf(r.nroFormulario!)}>
                            PDF
                          </Button>
                        ) : null}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </Box>
          )}
        </CardContent>
      </Card>

      <ContratoTiposDialog
        open={tiposOpen}
        onClose={() => setTiposOpen(false)}
        onChanged={() => {
          void cargarTipos();
        }}
      />

      {pdfUrl && (
        <PDFViewer
          pdfUrl={pdfUrl}
          onClose={() => {
            URL.revokeObjectURL(pdfUrl);
            setPdfUrl(null);
          }}
          title="Factura de contrato"
          documentId={pdfDocId}
          documentType="factura"
        />
      )}
    </Box>
  );
};

export default ContratosFacturacionPreview;
